import {
  forwardRef,
  HTMLAttributes,
  MouseEvent as ReactMouseEvent,
  useRef
} from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'

import Media, { MediaElement } from '@/components/base/media'
import Typography from '@/components/base/typography'
import { isBookingLink } from '@/utils/path'

import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type CardProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    media: MediaElement
    link: WPLink
    keywords: { list: { word: string }[] }
    description: string
    isThankYouPage: boolean
  }>

const Card = forwardRef<HTMLImageElement | HTMLVideoElement, CardProps>(
  (
    {
      media,
      link,
      className,
      keywords = { list: [] },
      isThankYouPage,
      description,
      ...props
    },
    ref
  ) => {
    const root = useRef(null!)

    const { contextSafe } = useGSAP({ scope: root })

    const handleMouse = contextSafe((e: ReactMouseEvent<HTMLAnchorElement>) => {
      const isHover = e.type === 'mouseenter'
      gsap.to('[data-motion-media]', {
        scale: isHover ? 1.07 : 1,
        duration: 2,
        ease: 'power2.out'
      })
    })

    return (
      <div
        className={cn(
          'relative aspect-[423/574]',
          'overflow-hidden will-change-transform after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/45',
          {
            'aspect-auto min-h-screen md:aspect-[423/574] md:min-h-0':
              isThankYouPage
          },
          className
        )}
        ref={root}
        {...props}
      >
        <Media ref={ref} fill fit="cover" {...media} data-motion-media />

        {isThankYouPage && description ? (
          <div className="absolute bottom-[4rem] left-margin z-10 lg:bottom-[6vh] lg:left-[6vh]">
            <Typography className="text-white">{description}</Typography>
          </div>
        ) : null}

        {keywords.list.map(({ word }, index) => (
          <div
            key={`keyword-${index}`}
            className={cn('absolute z-10', {
              'left-margin top-[4rem] lg:left-[6vh] lg:top-[6vh]': index === 0,
              'bottom-[4rem] left-margin lg:bottom-[6vh] lg:left-[6vh]':
                index === 1,
              'bottom-[4rem] right-margin lg:bottom-[6vh] lg:right-[6vh]':
                index === 2
            })}
          >
            <Typography className="text-[1.6rem] uppercase leading-[1.4rem] tracking-[0.1rem] md:text-[2rem]">
              {word}
            </Typography>
          </div>
        ))}

        {link ? (
          <Link
            href={link.url}
            target={link.target}
            scroll={false}
            className="absolute inset-0 z-10 h-full w-full will-change-transform after:absolute after:left-0 after:top-0 after:z-10 after:h-full after:w-full"
            onMouseEnter={handleMouse}
            onMouseLeave={handleMouse}
            data-gtm-event={isBookingLink(link.url) ? 'book-cta' : 'card-cta'}
          >
            <span className="sr-only">{link.title}</span>
          </Link>
        ) : null}
      </div>
    )
  }
)

export default Card
